import type {
  ConciseChangeset,
  ConciseWorkspace,
} from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryWorkspacesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryWorkspacesService'
import {
  assertNotEmpty,
  type ConfigurableServer,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { useQuery } from '@tanstack/react-query'

import { fetchConciseWorkspaceKey } from '../keys/configuration'
import type { BranchKey } from './types'

export const useFetchConciseWorkspace = ({
  workspaceName,
  server,
  enabled,
  shouldIncludeBaseConciseChangesets,
}: {
  workspaceName: Optional<string>
  server: Optional<ConfigurableServer>
  enabled: boolean
  shouldIncludeBaseConciseChangesets?: boolean
}) => {
  const isEnabled = !!enabled && !!workspaceName && !!server
  const queryKey = isEnabled
    ? fetchConciseWorkspaceKey({
        server,
        workspaceName,
        includeBaseChangesets: !!shouldIncludeBaseConciseChangesets,
      })
    : []
  // We intentionally use useQuery in order to avoid suspense from kicking in inside the branches panel,
  // making it snappier for users to click through branches and workspace in the branches tree.
  return useQuery({
    enabled: isEnabled,
    queryKey,
    queryFn: (): Promise<Optional<ConciseWorkspace>> => {
      if (!isEnabled) {
        return Promise.resolve(null)
      }
      assertNotEmpty(server)
      assertNotEmpty(workspaceName)
      return ConfigurationRepositoryWorkspacesService.workspaceServiceGetGetConciseWorkspace(
        {
          componentName: server.toLocaleLowerCase(),
          workspaceName,
          includeBaseChangesets: !!shouldIncludeBaseConciseChangesets,
        },
      )
    },
    select: (
      conciseWorkspace,
    ): (ConciseChangeset & {
      branchKey: BranchKey
    })[] =>
      // We're currently unable to tell follow changesets apart from base changesets, so we just assume that all baseChangesets belong to the base branch.
      // We're also unable to tell ancestor branch changesets apart from base changesets, so we just assume that all baseChangesets belong to the base branch.
      isEnabled
        ? [
            ...(conciseWorkspace?.baseChangesets
              ?.filter(notEmpty)
              ?.toReversed()
              .map((conciseChangeset) => {
                assertNotEmpty(conciseWorkspace.baseBranchName)
                assertNotEmpty(server)
                return {
                  ...conciseChangeset,
                  branchKey: {
                    name: conciseWorkspace.baseBranchName,
                    server,
                  },
                }
              }) ?? []),
          ]
        : [],
    staleTime: Infinity,
  })
}
